import { Color, Vector2, Vector4 } from 'three/webgpu'
import {
	uniform,
	uv,
	vec2,
	vec3,
	vec4,
	mrt,
	smoothstep,
	distance,
	sin,
	select,
	mix,
	interleavedGradientNoise,
	screenCoordinate,
} from 'three/tsl'
import RoundedImageMaterial from './RoundedImageMaterial'

export default class WarpPanelMaterial extends RoundedImageMaterial {
	static defaults = {
		top: 0x000000,
		bottom: 0x000000,
		warp: 0.8,
		waves: 3,
		ripple: 0.08,
		bounce: 1,
		falloff: 0.75,
		origin: new Vector2(1, 1),
	}

	static backInOut(t, bounce) {
		const c = 1.70158 * 1.525 * bounce
		const a = t.mul(2)
		const b = a.sub(2)

		return select(
			t.lessThan(0.5),
			a
				.mul(a)
				.mul(a.mul(c + 1).sub(c))
				.div(2),
			b
				.mul(b)
				.mul(b.mul(c + 1).add(c))
				.add(2)
				.div(2)
		)
	}

	static ease(t, bounce) {
		const c = 1.70158 * 1.525 * bounce
		const a = t * 2
		const b = a - 2

		return t < 0.5 ? (a * a * ((c + 1) * a - c)) / 2 : (b * b * ((c + 1) * b + c) + 2) / 2
	}

	offset(u, v, target) {
		const { warp, waves, ripple, bounce, falloff, origin } = this.shape
		const progress = this.progress.value
		const delta = this.delta.value
		const lag = Math.hypot(u - origin.x, v - origin.y) / Math.SQRT2
		const begin = (lag * warp) / (1 + warp)
		const t = Math.min(1, Math.max(0, (progress - begin) * (1 + warp)))
		const local = t * t * (3 - 2 * t)
		const wave = Math.sin((lag - progress) * waves * Math.PI * 2) * Math.sin(progress * Math.PI) * ripple
		const wobble = (WarpPanelMaterial.ease(local, bounce) - local + wave) * (1 - lag * falloff)
		const x = u
		const y = 1 - v

		return target.set(
			(delta.x + x * delta.z) * (local - progress) + (x - 0.5) * delta.z * wobble,
			(delta.y + y * delta.w) * (local - progress) + (y - 0.5) * delta.w * wobble
		)
	}

	constructor(options = {}) {
		const { top, bottom, warp, waves, ripple, bounce, falloff, origin, ...rest } = {
			...WarpPanelMaterial.defaults,
			...options,
		}

		super(rest)

		this.shape = { warp, waves, ripple, bounce, falloff, origin }
		this.delta = uniform(new Vector4())
		this.progress = uniform(0)

		const corner = vec2(uv().x, uv().y.oneMinus())
		const lag = distance(uv(), origin).div(Math.SQRT2)
		const envelope = sin(this.progress.mul(Math.PI))
		const wave = sin(lag.sub(this.progress).mul(waves * Math.PI * 2))
			.mul(envelope)
			.mul(ripple)
		const begin = lag.mul(warp / (1 + warp))
		const local = smoothstep(begin, begin.add(1 / (1 + warp)), this.progress)
		const wobble = WarpPanelMaterial.backInOut(local, bounce).sub(local).add(wave)
		const travel = this.delta.xy.add(corner.mul(this.delta.zw)).mul(local.sub(this.progress))
		const swell = corner.sub(0.5).mul(this.delta.zw).mul(wobble).mul(lag.mul(falloff).oneMinus())
		const shift = travel.add(swell).div(this.size)

		this.positionNode = vec3(uv().sub(0.5).add(vec2(shift.x, shift.y.negate())), 0)
		const steps = Math.max(1, ...[0, 8, 16].map(shift => Math.abs(((top >> shift) & 255) - ((bottom >> shift) & 255))))
		const grain = interleavedGradientNoise(screenCoordinate.xy).sub(0.5).div(steps)

		this.colorNode = mix(uniform(new Color(bottom)), uniform(new Color(top)), uv().y.add(grain).clamp())
		this.mrtNode = mrt({ velocity: vec4(0) })
	}
}
